import { useMemo } from 'react';
import { AlertTriangle, CalendarClock, Loader2, Save } from 'lucide-react';
import type { PurgePreview, RetentionUnit } from '../../types/retention';
import { formatOrdinalDate, formatOrdinalDateTime } from '../../utils/dateFormat';

export interface RetentionPurgeModalProps {
  open: boolean;
  /** Save request is in flight. */
  loading?: boolean;
  /** Singular noun for the records, e.g. "audit log" or "notification". */
  itemSingular: string;
  /** Plural noun for the records, e.g. "audit logs" or "notifications". */
  itemPlural: string;
  /** Controlled retention value (kept as a string so the input can be cleared). */
  value: string;
  unit: RetentionUnit;
  onValueChange: (value: string) => void;
  onUnitChange: (unit: RetentionUnit) => void;
  /** Live count/cutoff preview for the current window. */
  preview?: PurgePreview;
  previewLoading?: boolean;
  previewError?: boolean;
  /** When the cleanup job next runs (ISO timestamp). */
  nextRunAt?: string | null;
  /** IANA timezone the job runs in, e.g. "Asia/Dhaka". */
  timezone?: string;
  onClose: () => void;
  /** Called with the chosen retention window (remove records older than this). */
  onConfirm: (value: number, unit: RetentionUnit) => void;
}

const UNIT_OPTIONS: { value: RetentionUnit; label: string }[] = [
  { value: 'days', label: 'Day(s)' },
  { value: 'weeks', label: 'Week(s)' },
  { value: 'months', label: 'Month(s)' },
];

const PRESETS: { label: string; value: number; unit: RetentionUnit }[] = [
  { label: '7 days', value: 7, unit: 'days' },
  { label: '30 days', value: 30, unit: 'days' },
  { label: '90 days', value: 90, unit: 'days' },
  { label: '6 months', value: 6, unit: 'months' },
  { label: '1 year', value: 12, unit: 'months' },
];

/** Mirrors the backend cutoff calculation for instant feedback while preview loads. */
function computeCutoff(value: number, unit: RetentionUnit): Date {
  const cutoff = new Date();
  if (unit === 'weeks') cutoff.setDate(cutoff.getDate() - value * 7);
  else if (unit === 'months') cutoff.setMonth(cutoff.getMonth() - value);
  else cutoff.setDate(cutoff.getDate() - value);
  return cutoff;
}

/**
 * Shared retention settings dialog: records older than the chosen window
 * (days / weeks / months) are permanently deleted by the scheduled cleanup
 * job. Nothing is removed on save — the daily cron applies the window.
 * Used by both the Audit Logs and Notifications screens.
 */
export function RetentionPurgeModal({
  open,
  loading = false,
  itemSingular,
  itemPlural,
  value,
  unit,
  onValueChange,
  onUnitChange,
  preview,
  previewLoading = false,
  previewError = false,
  nextRunAt,
  timezone,
  onClose,
  onConfirm,
}: RetentionPurgeModalProps) {
  const parsedValue = Number(value);
  const isValid = Number.isInteger(parsedValue) && parsedValue > 0;

  const cutoffLabel = useMemo(() => {
    if (preview?.cutoff) return formatOrdinalDate(preview.cutoff);
    if (isValid) return formatOrdinalDate(computeCutoff(parsedValue, unit).toISOString());
    return '—';
  }, [preview?.cutoff, isValid, parsedValue, unit]);

  const nextRunLabel = nextRunAt ? formatOrdinalDateTime(nextRunAt) : '—';

  if (!open) return null;

  const unitNoun = UNIT_OPTIONS.find((o) => o.value === unit)?.label.toLowerCase() ?? unit;
  const count = preview?.count ?? 0;
  const nothingToRemove = !previewError && preview !== undefined && count === 0;
  // Saving is allowed even when nothing matches yet — the window stays set for future records.
  const confirmDisabled = loading || !isValid;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Schedule ${itemPlural} cleanup`}
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg shrink-0 bg-danger/10">
              <CalendarClock size={20} className="text-danger" />
            </div>
            <div className="space-y-1 min-w-0">
              <h3 className="text-base font-semibold text-text">Remove old {itemPlural}</h3>
              <p className="text-sm text-text-muted">
                Set how long {itemPlural} are kept. Anything older is permanently deleted by the
                automatic cleanup that runs every night at{' '}
                <span className="text-text font-medium">2:00 AM{timezone ? ` (${timezone})` : ''}</span>.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label
                htmlFor="retention-value"
                className="block text-xs font-medium text-text-muted uppercase mb-1.5"
              >
                Keep {itemPlural} for
              </label>
              <input
                id="retention-value"
                type="number"
                min={1}
                step={1}
                value={value}
                onChange={(e) => onValueChange(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
              />
            </div>
            <div className="sm:w-44">
              <label
                htmlFor="retention-unit"
                className="block text-xs font-medium text-text-muted uppercase mb-1.5"
              >
                Period
              </label>
              <select
                id="retention-unit"
                value={unit}
                onChange={(e) => onUnitChange(e.target.value as RetentionUnit)}
                disabled={loading}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
              >
                {UNIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {PRESETS.map((preset) => {
              const active = String(preset.value) === value && preset.unit === unit;
              return (
                <button
                  key={preset.label}
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    onValueChange(String(preset.value));
                    onUnitChange(preset.unit);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 ${
                    active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-text-muted hover:text-text hover:bg-background'
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>

          <div className="rounded-lg bg-background border border-border px-4 py-3 text-sm">
            {!isValid ? (
              <span className="text-danger">Enter a whole number of {unitNoun} greater than 0.</span>
            ) : previewLoading && !preview ? (
              <span className="flex items-center gap-2 text-text-muted">
                <Loader2 size={14} className="animate-spin" /> Calculating affected {itemPlural}…
              </span>
            ) : nothingToRemove ? (
              <span className="text-text-muted">
                Nothing older than <span className="text-text font-medium">{cutoffLabel}</span> right
                now. Newer {itemPlural} will be cleaned up once they age past this window.
              </span>
            ) : (
              <span className="text-text">
                <span className="font-semibold text-danger">
                  {previewError ? 'Unknown number of' : `${count} `}
                </span>
                {previewError ? itemPlural : `${itemSingular}${count === 1 ? '' : 's'}`} older than{' '}
                <span className="text-text font-medium">{cutoffLabel}</span> will be permanently
                deleted on the next run.
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 rounded-lg bg-background border border-border px-4 py-2.5 text-sm">
            <CalendarClock size={14} className="text-primary shrink-0" />
            <span className="text-text-muted">
              Next automatic cleanup:{' '}
              <span className="text-text font-medium">{nextRunLabel}</span>
            </span>
          </div>

          <div className="flex items-start gap-2 rounded-lg bg-danger/5 border border-danger/20 px-3 py-2">
            <AlertTriangle size={14} className="text-danger shrink-0 mt-0.5" />
            <p className="text-xs text-danger">
              This cannot be undone. Once the automatic cleanup runs, {itemPlural} older than the
              window are removed from the database permanently.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(parsedValue, unit)}
            disabled={confirmDisabled}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-danger text-white hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save cleanup settings
          </button>
        </div>
      </div>
    </div>
  );
}
